import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { KpiCard } from "./KpiCard";
import { TableSkeleton } from "./TableSkeleton";
import { tokens } from "../theme";
import { foyerApi } from "../http/foyer";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";
import { errorText } from "../utils/apiError";
import type {
  MeteredFleetMonth,
  MeteredMonthlyReport,
  MeteredPremiseMonthWire,
} from "@shared/meteredMonthly";

/**
 * The monthly metered-usage KPI (2026-09-23): what the reach department is
 * measured on. Per premise per month: did its signage reach its own target
 * (binary, against the pass line), and per month: what share of metered
 * premises passed, against the fleet goal. Both lines are set on General
 * settings. Closed months are frozen server-side; the running month is
 * provisional and says so.
 */

const pctFmt = (fmt: Formatters, n: number | null): string =>
  n === null
    ? "-"
    : fmt.number(n / 100, { style: "percent", maximumFractionDigits: 0 });
const hFmt = (t: Translate, fmt: Formatters, n: number): string =>
  t("analytics:meteredMonthlyView.hours", {
    value: fmt.number(n, { maximumFractionDigits: 1 }),
  });

function monthLabel(fmt: Formatters, key: string): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  return fmt.date(new Date(Date.UTC(y, m - 1, 1)), {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function cellColors(r: MeteredPremiseMonthWire): { fg: string; bg: string } {
  if (r.inProgress) return { fg: tokens.inkMuted, bg: "transparent" };
  return r.passed
    ? { fg: tokens.success, bg: tokens.successTint }
    : { fg: tokens.error, bg: tokens.errorTint };
}

function PremiseMonthCell({ r }: { r: MeteredPremiseMonthWire }) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const pct = (n: number | null) => pctFmt(fmt, n);
  const h = (n: number) => hFmt(t, fmt, n);
  const c = cellColors(r);
  const target =
    r.mode === "hours"
      ? t("meteredMonthlyView.targetHoursPerMonth", {
          hours: h(r.configuredValue),
        })
      : t("meteredMonthlyView.targetShareOfOpenHours", {
          value: fmt.number(r.configuredValue),
        });
  const detail = [
    t("meteredMonthlyView.detail.delivered", {
      actual: h(r.actualHours),
      target: h(r.targetHours),
      attainment: pct(r.attainmentPercent),
    }),
    t("meteredMonthlyView.detail.uptime", {
      uptime: pct(r.uptimePercent),
      open: h(r.openHours),
    }),
    r.mixed
      ? t("meteredMonthlyView.detail.targetMixed", { target })
      : t("meteredMonthlyView.detail.target", { target }),
    r.prorated ? t("meteredMonthlyView.detail.prorated") : null,
    r.noOpeningHours ? t("meteredMonthlyView.detail.noOpeningHours") : null,
    r.inProgress
      ? t("meteredMonthlyView.detail.inProgress")
      : r.frozen
        ? t("meteredMonthlyView.detail.frozen", {
            threshold: fmt.number(r.passThresholdPercent),
          })
        : t("meteredMonthlyView.detail.notFrozen"),
    r.screens.length > 1
      ? t("meteredMonthlyView.detail.pooled", {
          count: r.screens.length,
          screens: r.screens
            .map((s) =>
              t("meteredMonthlyView.detail.pooledScreen", {
                screen: s.roomId ?? s.screenId,
                attainment: pct(s.attainmentPercent),
              })
            )
            .join(", "),
        })
      : null,
  ]
    .filter(Boolean)
    .join("\n");
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{detail}</span>}>
      <Box
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-end",
          px: 1,
          py: 0.25,
          borderRadius: "6px",
          bgcolor: c.bg,
          color: c.fg,
          minWidth: 64,
        }}
        data-testid={`metered-cell-${r.monthKey}`}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: "fontWeightBold", color: "inherit" }}
        >
          {pct(r.attainmentPercent)}
        </Typography>
        <Typography variant="micro" sx={{ color: "inherit" }}>
          {r.inProgress
            ? t("meteredMonthlyView.cell.soFar")
            : r.passed
              ? t("meteredMonthlyView.cell.pass")
              : t("meteredMonthlyView.cell.fail")}
          {r.prorated ? t("meteredMonthlyView.cell.prorated") : ""}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function FleetMonthCell({ f }: { f: MeteredFleetMonth }) {
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const color =
    f.goalMet === null || f.inProgress
      ? tokens.inkMuted
      : f.goalMet
        ? tokens.success
        : tokens.error;
  return (
    <Box sx={{ textAlign: "right", color }} data-testid={`fleet-${f.monthKey}`}>
      <Typography sx={{ fontWeight: "fontWeightBold", color: "inherit" }}>
        {pctFmt(fmt, f.passRatePercent)}
      </Typography>
      <Typography variant="micro" sx={{ color: "inherit" }}>
        {f.counted > 0
          ? t("meteredMonthlyView.fleetCell.passed", {
              passed: f.passed,
              counted: f.counted,
            })
          : t("meteredMonthlyView.fleetCell.noneMetered")}
        {f.inProgress ? t("meteredMonthlyView.fleetCell.soFar") : ""}
      </Typography>
    </Box>
  );
}

/** The report as CSV - one row per premise per month, for KPI reporting
 *  outside the CMS. */
export function meteredMonthlyCsv(report: MeteredMonthlyReport): string {
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const round = (n: number | null) =>
    n === null ? "" : String(Math.round(n * 10) / 10);
  const header = [
    "premise_id",
    "premise",
    "month",
    "mode",
    "configured",
    "target_hours",
    "actual_hours",
    "open_hours",
    "uptime_percent",
    "attainment_percent",
    "passed",
    "pass_line_percent",
    "prorated",
    "in_progress",
    "frozen",
  ];
  const lines = [header.join(",")];
  for (const p of report.premises) {
    for (const m of report.months) {
      const r = p.months[m];
      if (!r) continue;
      lines.push(
        [
          p.premiseId,
          p.premiseName,
          m,
          r.mode,
          r.configuredValue,
          round(r.targetHours),
          round(r.actualHours),
          round(r.openHours),
          round(r.uptimePercent),
          round(r.attainmentPercent),
          r.inProgress ? "" : r.passed ? "yes" : "no",
          r.passThresholdPercent,
          r.prorated ? "yes" : "no",
          r.inProgress ? "yes" : "no",
          r.frozen ? "yes" : "no",
        ]
          .map(esc)
          .join(",")
      );
    }
  }
  return lines.join("\n");
}

function downloadCsv(report: MeteredMonthlyReport) {
  const blob = new Blob([meteredMonthlyCsv(report)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `metered-usage-${report.months[0] ?? ""}_${
    report.months[report.months.length - 1] ?? ""
  }.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function MeteredMonthlyView() {
  const [report, setReport] = useState<MeteredMonthlyReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation("analytics");
  const fmt = useFormat();
  const pct = (n: number | null) => pctFmt(fmt, n);

  useEffect(() => {
    let cancelled = false;
    foyerApi
      .fleetMeteredUsageMonthly({ months: 12 })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err) => {
        if (!cancelled) setError(errorText(err, t));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (error) {
    return <Typography sx={{ color: tokens.error }}>{error}</Typography>;
  }
  if (report === null) return <TableSkeleton rows={5} />;

  const closed = report.fleet.filter((f) => !f.inProgress && f.counted > 0);
  const lastClosed = closed[closed.length - 1];
  const running = report.fleet.find((f) => f.inProgress);

  return (
    <Box data-testid="metered-monthly">
      <Stack
        direction="row"
        sx={{ gap: 3, flexWrap: "wrap", mb: 3, alignItems: "stretch" }}
      >
        <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
          <KpiCard
            label={
              lastClosed
                ? t("meteredMonthlyView.kpi.lastClosed", {
                    month: monthLabel(fmt, lastClosed.monthKey),
                  })
                : t("meteredMonthlyView.kpi.lastClosedFallback")
            }
            value={lastClosed ? pct(lastClosed.passRatePercent) : "-"}
            sub={
              lastClosed
                ? t("meteredMonthlyView.kpi.lastClosedSub", {
                    passed: lastClosed.passed,
                    counted: lastClosed.counted,
                    goal: fmt.number(report.goalPercent),
                    outcome: lastClosed.goalMet
                      ? t("meteredMonthlyView.kpi.goalMet")
                      : t("meteredMonthlyView.kpi.goalMissed"),
                  })
                : t("meteredMonthlyView.kpi.noClosedMonth")
            }
            icon={<FlagOutlinedIcon />}
            tint={
              lastClosed?.goalMet === false
                ? tokens.errorTint
                : tokens.successTint
            }
            accent={
              lastClosed?.goalMet === false ? tokens.error : tokens.success
            }
          />
        </Box>
        <Box sx={{ flex: "1 1 240px", minWidth: 240 }}>
          <KpiCard
            label={
              running
                ? t("meteredMonthlyView.kpi.running", {
                    month: monthLabel(fmt, running.monthKey),
                  })
                : t("meteredMonthlyView.kpi.runningFallback")
            }
            value={running ? pct(running.passRatePercent) : "-"}
            sub={
              running
                ? t("meteredMonthlyView.kpi.runningSub", {
                    passed: running.passed,
                    counted: running.counted,
                  })
                : t("meteredMonthlyView.kpi.noRunning")
            }
            icon={<InsightsOutlinedIcon />}
            tint={tokens.surface}
            accent={tokens.inkMuted}
          />
        </Box>
      </Stack>

      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}
      >
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
          {t("meteredMonthlyView.passLine", {
            threshold: fmt.number(report.passThresholdPercent),
          })}
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => downloadCsv(report)}
          disabled={report.premises.length === 0}
          data-testid="metered-monthly-csv"
        >
          {t("meteredMonthlyView.csv")}
        </Button>
      </Stack>

      {report.premises.length === 0 ? (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
          data-testid="metered-monthly-empty"
        >
          <Typography
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {t("meteredMonthlyView.empty")}
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{ border: `1px solid ${tokens.divider}`, borderRadius: "12px" }}
        >
          <Table size="small" data-testid="metered-monthly-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("meteredMonthlyView.columns.premise")}</TableCell>
                {report.months.map((m) => (
                  <TableCell key={m} align="right">
                    {monthLabel(fmt, m)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: tokens.surface }}>
                <TableCell>
                  <Typography sx={{ fontWeight: "fontWeightBold" }}>
                    {t("meteredMonthlyView.fleet")}
                  </Typography>
                  <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
                    {t("meteredMonthlyView.fleetSub", {
                      goal: fmt.number(report.goalPercent),
                    })}
                  </Typography>
                </TableCell>
                {report.fleet.map((f) => (
                  <TableCell key={f.monthKey} align="right">
                    <FleetMonthCell f={f} />
                  </TableCell>
                ))}
              </TableRow>
              {report.premises.map((p) => (
                <TableRow key={p.premiseId} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                      {p.premiseName}
                    </Typography>
                  </TableCell>
                  {report.months.map((m) => (
                    <TableCell key={m} align="right">
                      {p.months[m] ? (
                        <PremiseMonthCell r={p.months[m]!} />
                      ) : (
                        <Typography
                          variant="meta"
                          sx={{ color: tokens.inkMuted }}
                        >
                          -
                        </Typography>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
